
import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import ProfileHeader from "@/components/profile/ProfileHeader";
import ProfileInfo from "@/components/profile/ProfileInfo";
import ProfileGallery from "@/components/profile/ProfileGallery";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";

const Profile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Redirect if not logged in
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Mutation to create a new profile
  const createProfileMutation = useMutation({
    mutationFn: async () => {
      console.log("Creating new profile for user:", user.id);
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          id: user.id,
          username: `user_${user.id.substring(0, 8)}` // Add default username
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
    },
  });

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['profile', user.id],
    queryFn: async () => {
      console.log("Fetching profile for user:", user.id);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      // If no profile found, create one
      if (!data && !error) {
        console.log("No profile found, creating new one");
        return createProfileMutation.mutateAsync();
      }
      
      if (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }
      
      console.log("Profile data:", data);
      return data;
    },
  });

  if (isLoading || createProfileMutation.isPending) {
    return (
      <div className="min-h-screen bg-[#1A1F2C] text-white flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (error || createProfileMutation.error) {
    return (
      <div className="min-h-screen bg-[#1A1F2C] text-white flex items-center justify-center">
        Error loading profile. Please try again later.
      </div>
    );
  }

  return (
    <PageLayout transparentHeader={true}>
      <div className="min-h-screen bg-[#1A1F2C] text-white relative">
        <ProfileHeader profile={profile} />
        
        <div className="relative px-4 md:px-6 pb-6 pt-24 md:pt-32 max-w-7xl mx-auto z-10">
          <div className="md:flex md:gap-12">
            <div className="md:w-80 lg:w-96">
              <ProfileInfo profile={profile} />
            </div>
            
            <div className="flex-1 mt-8 md:mt-16">
              <ProfileGallery userId={profile.id} />
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Profile;
